import { Component, OnInit } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRoute } from '@angular/router';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';

/**
 * Component for handling OAuth2 callback.
 *
 * This component is responsible for processing the OAuth2 callback URL,
 * handling errors, and notifying the parent window of the authorization result.
 */
@Component({
  selector: 'app-besharp-oauth2-callback',
  imports: [ProgressBarModule, DividerModule, MessageModule],
  templateUrl: './oauth2-callback.component.html',
  styleUrls: ['./oauth2-callback.component.scss'], // corrected styleUrl to styleUrls
})
export class OAuth2CallbackComponent implements OnInit {
  /**
   * Constructs a new instance of the component.
   *
   * @param route The activated route.
   */
  constructor(private route: ActivatedRoute) {}

  /**
   * The current status of the OAuth2 callback.
   *
   * Can be one of 'loading', 'success', or 'error'.
   */
  protected status: 'loading' | 'success' | 'error' = 'loading';

  /**
   * The error message to display if an error occurs.
   */
  protected errorMessage: string = 'Error during sign-in';

  /**
   * Initializes the component.
   *
   * Processes the OAuth2 callback URL, handles errors, and notifies the parent window of the authorization result.
   */
  ngOnInit(): void {
    // Close the window after a short delay
    setTimeout(() => window.close(), 1500);

    // Create a broadcast channel for communicating with the parent window
    const sessionChannel = new BroadcastChannel(`oauth2-callback`);

    // Get the authorization ID from the query parameters
    const authorizationId = this.route.snapshot.queryParamMap.get('state');

    // Check if the authorization ID is present
    if (!authorizationId) {
      // If not, set the status to error and log a message
      this.status = 'error';
      this.errorMessage = 'Authorization ID not found in the state query param';
      console.debug(this.errorMessage);
      return;
    }

    // Log the authorization ID for debugging purposes
    console.debug('Authorization ID: ' + authorizationId);

    // Check if an error occurred during authorization
    const error = this.route.snapshot.queryParamMap.get('error');
    if (error) {
      // If an error occurred, set the status to error and post a message to the parent window
      this.status = 'error';
      this.errorMessage = error;
      sessionChannel.postMessage({
        authorizationId: authorizationId,
        code: null,
        error: error,
      });
      return;
    }

    // Get the authorization code from the query parameters
    const code = this.route.snapshot.queryParamMap.get('code') ?? '';
    // Set the status to success and post a message to the parent window
    this.status = 'success';
    sessionChannel.postMessage({
      authorizationId: authorizationId,
      code: code,
      error: null,
    });
  }
}
